import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppContext } from "../../../common/context/Provider";
import { ACTIONS, EMPLOYEE_ROLE, Private_Routes } from "../../../constants";
import { AuthenticatedUserModel } from "../../../models/security/authenticatedUser";
import { useValidateOvertimeSchedule } from "../../../pages/overtime-schedule/hooks/useValidateOvertimeSchedule";
import authenticationService from "../../../services/authenticationService";
import { getActionService } from "../../../utils/getActionService";

export default function UserMenu() {
  const navigation = useNavigate();
  const queryClient = useQueryClient();

  const { validate } = useValidateOvertimeSchedule();

  const {
    langResources,
    userInfo,
    activeSkinConfig,
    setIsRequestHistoryModalOpen,
  } = useContext(AppContext);

  const [authenticatedUser, setAuthenticatedUser] =
    useState<AuthenticatedUserModel>();

  useEffect(() => {
    if (localStorage.getItem("user")) {
      const user = JSON.parse(localStorage.getItem("user") ?? "");
      setAuthenticatedUser(user);
    }
  }, []);

  const logout = () => {
    authenticationService.logout();
    document.location.href = "/";
  };

  const showManageRequests = () => {
    return (
      getActionService(userInfo.actions, ACTIONS["approved.scheduling"]) ||
      getActionService(userInfo.actions, ACTIONS["approve.interruptions"]) ||
      getActionService(userInfo.actions, ACTIONS["approve.covers"])
    );
  };

  const isUserEmployee = () => {
    return userInfo.role.name == EMPLOYEE_ROLE;
  };

  const showManageOvertimeSchedule = () => {
    return getActionService(
      userInfo.actions,
      ACTIONS["ProgrammedVsWorkedOvertimeModule"],
    );
  };

  const validateOvertimeScheduleOption = () => {
    if (!validate()) return;

    queryClient.invalidateQueries({ queryKey: ["overtime-schedule"] });
    navigation(Private_Routes.OVERTIME_SCHEDULE.index);
  };

  return (
    <div
      className="dropdown ms-sm-3 header-item topbar-user "
      style={{
        height: 55,
        background: activeSkinConfig.backgroundNavbarColor,
      }}
    >
      <button
        type="button"
        className="btn"
        id="page-header-user-dropdown"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        <span className="d-flex align-items-center">
          <img
            className="rounded-circle header-profile-user"
            src="/assets/images/users/user-dummy-img.jpg"
            alt=""
          />
          <span className="text-start ms-xl-2" id="text-username">
            <span
              className={`d-none d-xl-inline-block ms-1 fw-medium user-name-text ${activeSkinConfig.profileTextColor}`}
            >
              {authenticatedUser?.fullName}
            </span>
          </span>
        </span>
      </button>

      <div className="dropdown-menu dropdown-menu-end">
        <h6 className="dropdown-header">
          {langResources["header.user.menu.options.title"]}
        </h6>

        {!isUserEmployee() && (
          <Link className="dropdown-item" to="/profile">
            <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>{" "}
            <span className="align-middle">
              {langResources["header.user.menu.options.profile"]}
            </span>
          </Link>
        )}

        {!isUserEmployee() && (
          <a
            className="dropdown-item"
            href="https://smartaccess.blob.core.windows.net/smartaccess/Plantilla_Cargue.xlsx"
            download
          >
            <i className="ri-file-download-line text-muted fs-16 align-middle me-1"></i>{" "}
            <span className="align-middle">
              Descargar Plantilla de Programación
            </span>
          </a>
        )}

        {!isUserEmployee() &&
          getActionService(
            userInfo.actions,
            ACTIONS["setting-up-overtime-approval-flows"],
          ) && (
            <Link
              className="dropdown-item"
              to={Private_Routes.PAYROLL_NOVELTY_FLOWS.index}
            >
              <i className="  ri-stack-overflow-line text-muted fs-16 align-middle me-1"></i>
              <span className="align-middle">
                Configurar flujos de aprobación
              </span>
            </Link>
          )}

        {!isUserEmployee() &&
          getActionService(
            userInfo.actions,
            ACTIONS["audit.module.access"],
          ) && (
            <Link
              className="dropdown-item"
              to={Private_Routes.AUDIT_LOGS.index}
            >
              <i className="ri-shield-check-line text-muted fs-16 align-middle me-1"></i>
              <span className="align-middle">Auditoría</span>
            </Link>
          )}

        {!isUserEmployee() && showManageOvertimeSchedule() && (
          <p
            className="dropdown-item m-0 "
            onClick={validateOvertimeScheduleOption}
          >
            <i className="mdi mdi-debug-step-over text-muted fs-16 align-middle me-1"></i>
            <span className="align-middle">Gestion de tiempos extra</span>
          </p>
        )}

        {!isUserEmployee() && showManageRequests() && (
          <>
            <Link className="dropdown-item" to={"/" + Private_Routes.AUTHORIZE}>
              <i className=" ri-check-double-line text-muted fs-16 align-middle me-1"></i>
              <span className="align-middle">
                {langResources["header.user.menu.options.manage.requests"]}
              </span>
            </Link>

            <button
              className="dropdown-item"
              onClick={() => {
                setIsRequestHistoryModalOpen(true);
              }}
            >
              <i className="  ri-history-line text-muted fs-16 align-middle me-1"></i>
              <span className="align-middle">ver todas las solicitudes</span>
            </button>
          </>
        )}

        <a className="dropdown-item" onClick={logout} href="#">
          <i className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i>
          <span className="align-middle" data-key="t-logout">
            {langResources["header.user.menu.options.logout"]}
          </span>
        </a>
      </div>
    </div>
  );
}
